import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Loader2, ArrowLeft, Trophy, Check, X,
  Zap, RefreshCw,
} from "lucide-react";

/**
 * Physiology — body-system analogy generator + Body Challenge quiz game.
 *
 * Flow:
 *   1. Grid of 10 body systems. Click a card → generate analogy (1 credit).
 *   2. Analogy view shows image + analogy + story + key points, plus a
 *      "Start Body Challenge" button (0.5 credit) that launches a 5-scenario
 *      quiz with 4 choices each. Score + per-question explanations at end.
 */
export default function Physiology() {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // generated analogy doc
  const [generating, setGenerating] = useState(false);
  const [challenge, setChallenge] = useState(null); // in-progress session
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [challengeAnswers, setChallengeAnswers] = useState({});
  const [challengeResult, setChallengeResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/physiology/systems")
      .then((r) => setSystems(r.data.systems || []))
      .catch(() => setSystems([]))
      .finally(() => setLoading(false));
  }, []);

  const openSystem = async (sys) => {
    setGenerating(sys.id);
    try {
      const r = await api.post("/physiology/generate", { system_id: sys.id });
      setActive(r.data);
      setChallenge(null);
      setChallengeAnswers({});
      setChallengeResult(null);
      window.dispatchEvent(new Event("pm-billing-refresh"));
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 402 && d?.error === "out_of_quota") {
        toast.error(d.reason || "Out of credits — upgrade or buy a top-up to generate.");
      } else {
        toast.error("Couldn't generate — try again in a moment.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const startChallenge = async () => {
    if (!active) return;
    setChallengeLoading(true);
    setChallengeResult(null);
    try {
      const r = await api.post("/physiology/body-challenge/start", { system_id: active.system_id });
      setChallenge(r.data);
      setChallengeAnswers({});
      window.dispatchEvent(new Event("pm-billing-refresh"));
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 402 && d?.error === "out_of_quota") {
        toast.error(d.reason || "Out of credits — upgrade or buy a top-up to play.");
      } else {
        toast.error("Couldn't start Body Challenge — try again in a moment.");
      }
    } finally {
      setChallengeLoading(false);
    }
  };

  const submitChallenge = async () => {
    if (!challenge) return;
    const answers = challenge.scenarios.map((_, i) => challengeAnswers[i] ?? -1);
    setSubmitting(true);
    try {
      const r = await api.post("/physiology/body-challenge/submit", {
        session_id: challenge.session_id,
        answers,
      });
      setChallengeResult(r.data);
    } catch (e) {
      toast.error("Couldn't submit — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // --- RESULTS SCREEN ------------------------------------------------------
  if (challengeResult) {
    const pct = Math.round((challengeResult.score / challengeResult.total) * 100);
    return (
      <div className="max-w-3xl mx-auto" data-testid="body-challenge-results">
        <button
          onClick={() => { setChallengeResult(null); setChallenge(null); setChallengeAnswers({}); }}
          className="text-sm mb-4 inline-flex items-center gap-1 hover:opacity-70"
          style={{ color: "var(--text-muted)" }}
          data-testid="challenge-back-btn"
        >
          <ArrowLeft size={14} /> Back to {challengeResult.system_name}
        </button>
        <div className="pm-card p-8 mb-6 text-center" style={{ background: pct >= 80 ? "var(--hl-green)" : pct >= 50 ? "var(--hl-yellow)" : "var(--hl-red)" }}>
          <Trophy size={48} className="mx-auto mb-3" style={{ color: pct >= 80 ? "#4f7942" : pct >= 50 ? "#c98a3b" : "#c94a3b" }} />
          <div className="font-display text-4xl font-bold mb-1" data-testid="challenge-score">
            {challengeResult.score} / {challengeResult.total}
          </div>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            {pct >= 80 ? "Mastery mode — this system is yours." :
             pct >= 50 ? "Solid foundation. One more pass and you'll own it." :
             "Early reps — review the explanations below and run it back."}
          </p>
        </div>
        <div className="space-y-3">
          {challengeResult.details.map((d, i) => (
            <div
              key={i}
              className="pm-card p-4"
              style={{ borderLeft: `4px solid ${d.is_correct ? "#4f7942" : "#c94a3b"}` }}
              data-testid={`challenge-detail-${i}`}
            >
              <div className="flex items-start gap-2 mb-2">
                {d.is_correct ? <Check size={16} className="shrink-0 mt-0.5" style={{ color: "#4f7942" }} /> : <X size={16} className="shrink-0 mt-0.5" style={{ color: "#c94a3b" }} />}
                <div className="font-semibold text-sm">{d.scenario}</div>
              </div>
              <div className="text-xs mb-1" style={{ color: "var(--text-secondary)" }}>
                You picked: <b>{d.picked >= 0 ? d.choices[d.picked] : "(skipped)"}</b>
              </div>
              {!d.is_correct && (
                <div className="text-xs mb-1" style={{ color: "#4f7942" }}>
                  Correct: <b>{d.choices[d.correct_index]}</b>
                </div>
              )}
              {d.explanation && (
                <div className="text-xs italic mt-1" style={{ color: "var(--text-muted)" }}>
                  {d.explanation}
                </div>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={startChallenge}
          disabled={challengeLoading}
          className="pm-btn-primary mt-6 inline-flex items-center gap-1.5 text-sm"
          data-testid="challenge-again-btn"
        >
          {challengeLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Play again · 0.5 credit
        </button>
      </div>
    );
  }

  // --- IN-PROGRESS CHALLENGE ----------------------------------------------
  if (challenge) {
    const answered = Object.keys(challengeAnswers).length;
    const total = challenge.scenarios.length;
    return (
      <div className="max-w-3xl mx-auto" data-testid="body-challenge-play">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => { setChallenge(null); setChallengeAnswers({}); }}
            className="text-sm inline-flex items-center gap-1 hover:opacity-70"
            style={{ color: "var(--text-muted)" }}
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            {answered}/{total} answered
          </div>
        </div>
        <div className="mb-6">
          <div className="pm-badge inline-flex mb-2" style={{ background: "var(--hl-blue)", color: "var(--primary)" }}>
            <Zap size={10} className="mr-1" /> {challenge.emoji} {challenge.system_name} Challenge
          </div>
          <h1 className="font-display text-3xl font-bold">Body Challenge</h1>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            Pick the correct physiological response for each scenario.
          </p>
        </div>
        <div className="space-y-5">
          {challenge.scenarios.map((s, i) => (
            <div key={i} className="pm-card p-5" data-testid={`challenge-scenario-${i}`}>
              <div className="font-semibold mb-3 flex items-start gap-2">
                <span className="pm-badge shrink-0" style={{ background: "var(--muted)" }}>#{i + 1}</span>
                <span>{s.scenario}</span>
              </div>
              <div className="grid gap-2">
                {s.choices.map((c, ci) => {
                  const picked = challengeAnswers[i] === ci;
                  return (
                    <button
                      key={ci}
                      onClick={() => setChallengeAnswers({ ...challengeAnswers, [i]: ci })}
                      className="text-left px-3 py-2 rounded-xl border text-sm transition-colors"
                      style={{
                        borderColor: picked ? "var(--primary)" : "var(--border)",
                        background: picked ? "var(--hl-blue)" : "var(--paper)",
                        color: picked ? "var(--primary)" : "var(--text)",
                        fontWeight: picked ? 600 : 400,
                      }}
                      data-testid={`challenge-choice-${i}-${ci}`}
                    >
                      {String.fromCharCode(65 + ci)}. {c}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={submitChallenge}
          disabled={submitting || answered < total}
          className="pm-btn-primary mt-6 w-full inline-flex items-center justify-center gap-1.5 text-base"
          data-testid="challenge-submit-btn"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Trophy size={16} />}
          {answered < total ? `Answer all ${total} to see your score` : "Submit & see score"}
        </button>
      </div>
    );
  }

  // --- ACTIVE ANALOGY ------------------------------------------------------
  if (active) {
    return (
      <div className="max-w-4xl mx-auto" data-testid="physiology-analogy">
        <button
          onClick={() => setActive(null)}
          className="text-sm mb-4 inline-flex items-center gap-1 hover:opacity-70"
          style={{ color: "var(--text-muted)" }}
          data-testid="physiology-back-btn"
        >
          <ArrowLeft size={14} /> All body systems
        </button>
        <div className="grid md:grid-cols-5 gap-6">
          <div className="md:col-span-2">
            {active.image_url ? (
              <img src={active.image_url} alt={active.system_name} className="w-full rounded-2xl" data-testid="physiology-img" />
            ) : (
              <div className="aspect-square flex items-center justify-center rounded-2xl text-6xl" style={{ background: "var(--muted)" }}>
                {active.emoji}
              </div>
            )}
          </div>
          <div className="md:col-span-3">
            <div className="pm-badge mb-2 inline-flex" style={{ background: "var(--hl-blue)", color: "var(--primary)" }}>
              {active.emoji} {active.system_name}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold mb-3">The Analogy</h1>
            <p className="text-lg mb-5 font-medium">{active.analogy}</p>
            <h2 className="font-display text-xl font-bold mb-2">The Story</h2>
            <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>{active.story}</p>
            <h2 className="font-display text-xl font-bold mb-2">Key Points</h2>
            <ul className="space-y-1 mb-6 text-sm" style={{ color: "var(--text-secondary)" }}>
              {(active.key_points || []).map((kp, i) => (
                <li key={i} className="flex gap-2"><span style={{ color: "var(--primary)" }}>•</span><span>{kp}</span></li>
              ))}
            </ul>
            <button
              onClick={startChallenge}
              disabled={challengeLoading}
              className="pm-btn-primary inline-flex items-center gap-1.5 text-sm"
              data-testid="physiology-start-challenge-btn"
            >
              {challengeLoading ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              Start Body Challenge · 0.5 credit
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- SYSTEMS GRID --------------------------------------------------------
  return (
    <div data-testid="physiology-page">
      <h1 className="font-display text-4xl font-bold mb-3">Physiology</h1>
      <p className="text-lg mb-8" style={{ color: "var(--text-secondary)" }}>
        Pick a body system — AI builds you a vivid analogy with an illustration, a story,
        and the highest-yield facts. Then test yourself with the Body Challenge quiz.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
          <Loader2 size={14} className="animate-spin" /> Loading body systems…
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="physiology-grid">
          {systems.map((s) => (
            <button
              key={s.id}
              onClick={() => openSystem(s)}
              disabled={generating === s.id}
              className="pm-card pm-card-hover p-5 text-left disabled:opacity-60"
              data-testid={`physiology-system-${s.id}`}
            >
              <div className="text-4xl mb-2">{s.emoji}</div>
              <div className="font-display font-bold text-lg mb-1">{s.name}</div>
              <p className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>{s.blurb}</p>
              <div className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: "var(--primary)" }}>
                {generating === s.id ? (
                  <><Loader2 size={12} className="animate-spin" /> Generating…</>
                ) : (
                  <><Sparkles size={12} /> Generate · 1 credit</>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
